import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Grid,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import ErrorIcon from "@mui/icons-material/Error";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs, { Dayjs } from "dayjs";
import { downloadSupplyPlanForecastTemplate } from "api/resources/supplyPlan";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  selectProductionParks,
  selectClientSupplyPlanForecast,
} from "store/selectors";
import {
  fetchAllProductionPark,
  fetchClientSupplyPlanForecast,
} from "store/appSlice";
import { downloadXLSX } from "helpers/excelExport";
import FileUpload from "components/FileUpload";
import Select from "components/Select";
import {
  saveClientSupplyPlanForecast,
  uploadClientSupplyPlanForecast,
} from "api/resources/clients";
import { get15MinStartTimesForDateInUTC } from "helpers/timeIntervals";

interface ForecastProps {
  selectedAuction: number;
  date: Dayjs;
  setSnackbar: (snackbar: { type: string; message: string }) => void;
  currentRoleClientId: number;
}

interface PeriodQuantity {
  date: string;
  period: string;
  hour: string;
  value: string;
  disabled?: boolean;
}

const Forecast = ({
  currentRoleClientId,
  selectedAuction,
  date,
  setSnackbar,
}: ForecastProps) => {
  const [periodQuantities, setPeriodQuantities] = useState([]);
  const [productionParkOptions, setProductionParkOptions] = useState([]);
  const [selectedProductionPark, setSelectedProductionPark] = useState<
    number | null
  >(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [valuesChanged, setValuesChanged] = useState(false);
  const [isProducer, setIsProducer] = useState(false);

  const dispatch = useAppDispatch();
  const clientSupplyPlanForecast = useAppSelector(
    selectClientSupplyPlanForecast,
  );
  const productionParks = useAppSelector(selectProductionParks);

  useEffect(() => {
    dispatch(fetchAllProductionPark(0, 1000));
  }, []);

  useEffect(() => {
    dispatch(
      fetchClientSupplyPlanForecast(
        currentRoleClientId,
        selectedAuction,
        dayjs(date).format("YYYY-MM-DD"),
        selectedProductionPark,
      ),
    );
  }, [selectedProductionPark, selectedAuction, dispatch, date]);

  useEffect(() => {
    const options: React.SetStateAction<any[]> = [];

    if (productionParks) {
      if (productionParks.content.length > 0) {
        setIsProducer(true);
      }

      productionParks.content.map((park) => {
        options.push({ label: park.name, value: park.id });
      });

      setProductionParkOptions(options);

      if (productionParks.content.length === 1) {
        setSelectedProductionPark(productionParks.content[0].id);
      }
    }
  }, [productionParks]);

  const periodStartsInUTC = get15MinStartTimesForDateInUTC(
    dayjs(date).format("YYYY-MM-DD"),
  );

  const TZ = "Europe/Tallinn";

  const generateTable = () => {
    // Build DST-aware 15-minute timestamps
    const start = dayjs.tz(date, TZ).startOf("day");
    const nextDateStr = start.add(1, "day").format("YYYY-MM-DD");
    const end = dayjs.tz(`${nextDateStr} 01:00`, "YYYY-MM-DD HH:mm", TZ);

    const actualPeriods = [];
    let current = start;
    while (current.isBefore(end)) {
      actualPeriods.push(current);
      current = current.add(15, "minute");
    }

    // Build table rows
    const periodQuantitiesCopy = actualPeriods.map((periodStart, i) => {
      const periodEnd = periodStart.add(15, "minute");

      const periodLabel = `${periodStart.format("HH:mm")} - ${periodEnd.format("HH:mm")}`;
      const formattedDate = periodStart.format("YYYY-MM-DD");

      const timeInUTC =
        dayjs(periodStart).utc().format("YYYY-MM-DD") +
        " " +
        periodStartsInUTC[i] +
        ":00";

      return {
        date: formattedDate, // Calendar date in Tallinn
        dateTime: periodStart, // Start of period as Day.js object
        periodIndex: i, // Numeric index
        time: periodLabel, // "HH:mm - HH:mm"
        value: clientSupplyPlanForecast
          ? Number(
              clientSupplyPlanForecast?.values.find((sp) => {
                const utcTime = dayjs(`${sp.date} ${sp.time}`).format(
                  "YYYY-MM-DD HH:mm:ss",
                );

                return utcTime === timeInUTC;
              })?.quantity,
            )
          : null,
        disabled: i < 4, // first few periods disabled if needed
      };
    });

    setPeriodQuantities(periodQuantitiesCopy);
  };

  useEffect(() => {
    generateTable();
  }, [date, clientSupplyPlanForecast]);

  const handleSaveForecast = () => {
    const postData = {
      auctionTypeId: selectedAuction,
      productionParkId: selectedProductionPark || null,
      forecastDate: dayjs(date).format("YYYY-MM-DD"),
      values: periodQuantities.slice(4).map((period, periodIndex) => {
        const [startTime] = period.time.split(" - ");

        const localStart = dayjs.tz(
          `${period.date} ${startTime}`,
          "YYYY-MM-DD HH:mm",
          TZ,
        );

        const utcDate = localStart.utc();
        const utcTime = periodStartsInUTC[periodIndex + 4];

        return {
          datetime: dayjs(utcDate).format("YYYY-MM-DD") + "T" + utcTime + ":00",
          quantity: Number(period.value),
        };
      }),
    };

    saveClientSupplyPlanForecast(currentRoleClientId, postData)
      .then(() => {
        setSnackbar({
          type: "success",
          message: "Forecast saved successfully",
        });
        dispatch(
          fetchClientSupplyPlanForecast(
            currentRoleClientId,
            selectedAuction,
            dayjs(date).format("YYYY-MM-DD"),
            selectedProductionPark,
          ),
        );
      })
      .catch(() => {
        setSnackbar({
          type: "error",
          message: "Error saving forecast",
        });
      });
  };

  const handleDownloadForecastTemplate = async () => {
    downloadSupplyPlanForecastTemplate(dayjs(date).format("YYYY-MM-DD")).then(
      (result) => {
        downloadXLSX(result, "Forecast_template.xlsx");
      },
    );
  };

  const handleUpload = () => {
    setUploading(true);
    uploadClientSupplyPlanForecast(
      currentRoleClientId,
      selectedAuction,
      uploadedFile,
      selectedProductionPark,
    )
      .then(() => {
        dispatch(
          fetchClientSupplyPlanForecast(
            currentRoleClientId,
            selectedAuction,
            dayjs(date).format("YYYY-MM-DD"),
            selectedProductionPark,
          ),
        );
        setSnackbar({
          type: "success",
          message: "Forecast uploaded successfully",
        });
        setUploadedFile(null);
        setUploading(false);
      })
      .catch(() => {
        setSnackbar({
          type: "error",
          message: "Error uploading forecast",
        });
        setUploading(false);
      });
  };

  return (
    <Grid container columnSpacing={8} sx={{ mt: "1rem" }}>
      <Grid item sm={6}>
        {productionParkOptions.length > 0 && (
          <Select
            label="Select production park"
            id="productionPark"
            name="productionPark"
            minWidth={380}
            maxWidth={380}
            value={selectedProductionPark ?? ""}
            options={productionParkOptions}
            onChange={(e) => setSelectedProductionPark(e.target.value)}
          />
        )}
        {((isProducer && selectedProductionPark) ||
          (!isProducer && productionParks)) && (
          <Box sx={{ mt: 3 }}>
            <FileUpload
              uploadedFile={uploadedFile}
              setUploadedFile={setUploadedFile}
              handleUpload={handleUpload}
              loading={uploading}
            />
          </Box>
        )}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          {valuesChanged && (
            <Button
              sx={{ mt: 1 }}
              variant="contained"
              onClick={() => handleSaveForecast()}
            >
              Save
            </Button>
          )}
        </Box>
        {((isProducer &&
          productionParkOptions.length > 0 &&
          selectedProductionPark) ||
          (!isProducer && productionParks)) && (
          <TableContainer component={Paper} sx={{ maxWidth: 420, mt: 2 }}>
            <Table sx={{ maxWidth: 420 }} size="small">
              <TableHead sx={{ bgcolor: "#eeeeee" }}>
                <TableRow>
                  <TableCell>Period (Europe/Tallinn)</TableCell>
                  <TableCell>Capacity (kW)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periodQuantities.map((row, rowIndex) => (
                  <TableRow
                    key={`Row-${dayjs(row.date).format("DD.MM.YYYY")}-${row.time}-${rowIndex}`}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          component="p"
                          sx={{ color: grey[400], mr: 1 }}
                        >
                          {dayjs(row.date).format("DD.MM.YYYY")}
                        </Typography>
                        <Typography component="p">{row.time}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          id="coefficient"
                          type="number"
                          variant="standard"
                          size="small"
                          value={row.value || ""}
                          onChange={(e) => {
                            const periodQuantitiesCopy: PeriodQuantity[] = [
                              ...periodQuantities,
                            ];
                            setValuesChanged(true);

                            periodQuantitiesCopy[rowIndex].value =
                              e.target.value;

                            setPeriodQuantities(periodQuantitiesCopy);
                          }}
                          disabled={row.disabled}
                          sx={{ maxWidth: "110px" }}
                        />
                        <Box sx={{ width: 24, flexShrink: 0 }}>
                          {rowIndex === 4 &&
                            periodQuantities[4]?.value !== "" &&
                            periodQuantities[4]?.value != null && (
                              <Tooltip title="Apply to all">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const updated = periodQuantities.map(
                                      (r, i) => {
                                        if (i < 4) return r;
                                        return {
                                          ...r,
                                          value: periodQuantities[4].value,
                                        };
                                      },
                                    );
                                    setPeriodQuantities(updated);
                                    setValuesChanged(true);
                                  }}
                                  sx={{ p: 0.25 }}
                                >
                                  <KeyboardArrowDownIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
      <Grid item sm={6}>
        <Typography component="p" sx={{ mb: 1 }}>
          {`Please insert your forecasted production capacities for the date
          (${dayjs(date).format("DD.MM.YYYY")}) in kilowatts (kW).`}
          <br />
          You may upload capacities by file or manually from in the form.
        </Typography>
        <Typography component="p" sx={{ mb: 4 }}>
          <strong>Notice:</strong> These capacities are taken into account for
          the next trading period.
        </Typography>
        <Typography
          component="p"
          sx={{
            fontWeight: 500,
            fontSize: "18px",
            mb: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ErrorIcon color="primary" sx={{ mr: 1 }} />
          <strong>Important:</strong>
        </Typography>
        <Typography component="p">
          Please enter your electricity values carefully:
        </Typography>
        <ul>
          <li>
            Use <strong>negative numbers (-)</strong> to indicate{" "}
            <strong>production</strong> (when you generate electricity).
          </li>
          <li>
            Use <strong>positive numbers (+)</strong> to indicate{" "}
            <strong>consumption</strong> (when you use electricity).
          </li>
        </ul>
        <Typography component="p" sx={{ mt: "1rem" }}>
          For example:
        </Typography>
        <ul>
          <li>-150 means you're producing at 150 kW capacity</li>
          <li>200 means you're consuming at 200 kW capacity</li>
        </ul>
        <Typography component="p" sx={{ mt: "1rem" }}>
          Entering the wrong sign may result in incorrect calculations.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleDownloadForecastTemplate()}
          sx={{ mt: 2 }}
        >
          Download template
        </Button>
      </Grid>
    </Grid>
  );
};

export default Forecast;
